import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import SettingsIcon from '@mui/icons-material/Settings';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavDrawer } from '../../shared/contexts/NavDrawerContext';
import { LOOKBOOK_CONTEXT_KEY } from '../../pages/CataloguePage';

const NAV_ITEMS = [
  { label: 'Product List', icon: <ListIcon />, path: '/' },
  { label: 'My Portfolios', icon: <FolderSpecialIcon />, path: '/portfolios' },
  { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
] as const;

export const NavDrawer: React.FC = () => {
  const { open, closeDrawer } = useNavDrawer();
  const navigate = useNavigate();
  const location = useLocation();

  const hasLookbookContext = !!localStorage.getItem(LOOKBOOK_CONTEXT_KEY);

  const handleNav = (path: string) => {
    navigate(path);
    closeDrawer();
  };

  const handleOpenLookbook = () => {
    navigate('/lookbook');
    closeDrawer();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={closeDrawer}
      PaperProps={{
        sx: {
          width: 260,
          bgcolor: '#363D4C',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#F1C3DB', letterSpacing: 1 }}>
          Navigation
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

      <List sx={{ flex: 1, py: 1 }}>
        {NAV_ITEMS.map(item => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => handleNav(item.path)}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              color: '#fff',
              '&.Mui-selected': {
                bgcolor: 'rgba(241,195,219,0.15)',
                color: '#F1C3DB',
                '& .MuiListItemIcon-root': { color: '#F1C3DB' },
              },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
            />
          </ListItemButton>
        ))}

        <ListItemButton
          disabled={!hasLookbookContext}
          onClick={handleOpenLookbook}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            color: hasLookbookContext ? '#fff' : 'rgba(255,255,255,0.35)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <MenuBookIcon />
          </ListItemIcon>
          <ListItemText
            primary="Current Lookbook"
            secondary={!hasLookbookContext ? 'No active session' : undefined}
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}
          />
        </ListItemButton>
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
      <List sx={{ py: 1 }}>
        <ListItemButton
          onClick={() => handleNav('/change-password')}
          sx={{
            borderRadius: 1,
            mx: 1,
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <LockResetIcon />
          </ListItemIcon>
          <ListItemText primary="Change password" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
        </ListItemButton>
      </List>
    </Drawer>
  );
};

export default NavDrawer;
